import { useCallback, useEffect, useRef, useState } from "react";
import { useAccessToken } from "@/features/auth/useAccessToken";
import { getIndex, type IndexDetailOut } from "@/shared/api/indexes";

/** How often a building collection is re-read. */
export const BUILD_POLL_MS = 2000;

/**
 * Load one collection and keep it fresh while it builds: re-reads
 * `GET /indexes/{id}` every `BUILD_POLL_MS` while `status === "building"`, and
 * stops as soon as the build is over (or the component unmounts).
 */
export function useCollection(indexId: string) {
  const getToken = useAccessToken();
  const [detail, setDetail] = useState<IndexDetailOut | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const alive = useRef(true);

  const reload = useCallback(async () => {
    try {
      const token = await getToken();
      const next = await getIndex(token, indexId);
      if (alive.current) {
        setDetail(next);
        setError(null);
      }
    } catch (err) {
      if (alive.current) setError(err instanceof Error ? err.message : String(err));
    } finally {
      if (alive.current) setLoading(false);
    }
  }, [getToken, indexId]);

  useEffect(() => {
    alive.current = true;
    setLoading(true);
    void reload();
    return () => {
      alive.current = false;
    };
  }, [reload]);

  const building = detail?.status === "building";
  useEffect(() => {
    if (!building) return;
    const timer = setInterval(() => void reload(), BUILD_POLL_MS);
    return () => clearInterval(timer);
  }, [building, reload]);

  return { detail, setDetail, loading, error, reload };
}
